import React, { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { Modal, ModalBody, TabContent, Nav, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { Button, Icon, UserAvatar } from "../../../components/Component";
import { findUpper } from "../../../utils/Utils";
import getTextColor from "../../../components/custom/getTextColor";
import { igAssignAgent } from "../../../services/igAdapter";
import { serviceGetUsersByRoleId } from "../../../services/users";
import { useDebounce } from "use-debounce";
import SimpleBar from "simplebar-react";
import moment from "moment";
import Swal from "sweetalert2";

/**
 * IgChatModalAssign — modal assign conversation ke agent.
 *
 * Props:
 *   isOpen: boolean
 *   onClose: function()
 *   chatId: string — conversation_id yang akan di-assign
 *   onAssigned: function(userName) — dipanggil setelah assign berhasil
 */
const IgChatModalAssign = ({ isOpen, onClose, chatId, onAssigned }) => {
  const [cookies] = useCookies(["SIDikey", "departmentIds", "id"]);
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState("");
  const [choosenDept, setChoosenDept] = useState("All Department");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggleDropDown = () => setDropdownOpen(!dropdownOpen);

  const [selectedUserId, setSelectedUserId] = useState("");
  const [userList, setUserList] = useState([]);
  const [salesList, setSalesList] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);

  const [searchText] = useDebounce(search, 100);

  const userDetail = JSON.parse(localStorage.getItem("userDetail") || "{}");
  const roleFunctions = JSON.parse(localStorage.getItem("permission") || "{}")?.departments?.access || [];

  const refreshUserList = async () => {
    setSearch("");
    const deptIds = roleFunctions.includes("Access_All_Dept") ? "" : cookies.departmentIds;
    const res = await serviceGetUsersByRoleId(cookies.SIDikey, deptIds, "SALES,CS");
    if (res?.data?.data) {
      const data = res.data.data.filter((u) => u.isActive !== false).sort((a, b) => b.status.localeCompare(a.status));
      setUserList(data);
      setSalesList(data);
    }
  };

  const refreshUserListDept = async (deptId) => {
    const res = await serviceGetUsersByRoleId(cookies.SIDikey, [deptId], "SALES,CS");
    if (res?.data?.data) {
      const data = res.data.data.filter((u) => u.isActive !== false).sort((a, b) => b.status.localeCompare(a.status));
      setUserList(data);
      setSalesList(data);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSearch("");
    } else {
      setSelectedUserId("");
      refreshUserList();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchText !== "") {
      const newUsers = userList.filter((u) => {
        const fullName = u.firstName + " " + u.lastName;
        return fullName.toLowerCase().includes(searchText.toLowerCase());
      });
      setFilteredUsers(newUsers);
      setSalesList(newUsers);
    } else {
      refreshUserList();
      setFilteredUsers(userList);
    }
  }, [searchText]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAssign = async () => {
    if (!selectedUserId || !chatId) return;
    const targetUserToAssign = userList.find((u) => u.id === selectedUserId);
    if (targetUserToAssign?.isLeadReceptionActive === false) {
      Swal.fire(
        "Failed",
        `User is currently not accepting leads${targetUserToAssign.leadReceptionReason ? `: ${targetUserToAssign.leadReceptionReason}` : ""}`,
        "error"
      );
      return;
    }
    setIsAssigning(true);
    const actorDetail = JSON.parse(localStorage.getItem("userDetail") || "{}");
    const actorName = `${actorDetail.firstName || ""} ${actorDetail.lastName || ""}`.trim();
    const targetUser = userList.find((u) => u.id === selectedUserId);
    const targetName = targetUser ? `${targetUser.firstName || ""} ${targetUser.lastName || ""}`.trim() : "";
    const res = await igAssignAgent(chatId, selectedUserId, actorName, "", targetName);
    setIsAssigning(false);
    if (res?.data?.conversation_id) {
      onAssigned(targetName);
      onClose();
    }
  };

  const renderUserCard = (item, index) => {
    const isLeadReceptionInactive = item.isLeadReceptionActive === false;
    return (
    <div
      className="card-inner-md"
      key={index}
      style={{ cursor: isLeadReceptionInactive ? "not-allowed" : "pointer", opacity: isLeadReceptionInactive ? 0.5 : 1 }}
      onClick={() => {
        if (isLeadReceptionInactive) return;
        if (item.id === selectedUserId) {
          setSelectedUserId("");
        } else {
          setSelectedUserId(item.id);
        }
      }}
    >
      <div className="user-card">
        <Icon
          key={item.id}
          className={item.id === selectedUserId ? "mr-1" : "invisible mr-1"}
          name="check-circle-fill"
          style={{ fontSize: 18, color: "#4461f2" }}
        />
        <UserAvatar theme={item.theme} text={findUpper(item.firstName)}>
          {item.status === "ONLINE" ? (
            <span className="status dot dot-lg dot-success"></span>
          ) : (
            <span className="status dot dot-lg dot-gray"></span>
          )}
        </UserAvatar>
        <div className="user-info">
          <span className="lead-text">{item.firstName + " " + item.lastName}</span>
          <span className="sub-text">{item.email}</span>
          {isLeadReceptionInactive && (
            <span className="sub-text text-danger">
              Not accepting leads{item.leadReceptionReason ? `: ${item.leadReceptionReason}` : ""}
              {item.leadReceptionResumeAt
                ? ` (resumes at ${moment.utc(item.leadReceptionResumeAt).local().format("DD/MM/YYYY HH:mm")})`
                : ""}
            </span>
          )}
        </div>
        <div className="user-action">
          {item.departments?.length > 0
            ? item.departments.map((department) => (
                <span
                  key={department.id}
                  className="badge rounded-pill text-bg-primary"
                  style={{
                    background: department.color || "#ffffff",
                    color: getTextColor(department.color || "#ffffff"),
                  }}
                >
                  {department.name}
                </span>
              ))
            : ""}
        </div>
      </div>
    </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      backdrop="static"
      keyboard={false}
      toggle={onClose}
      className="modal-dialog-centered"
    >
      <ModalBody>
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            onClose();
          }}
          className="close"
        >
          <Icon name="cross-sm"></Icon>
        </a>

        <Nav tabs>
          <Dropdown nav isOpen={dropdownOpen} toggle={toggleDropDown}>
            <DropdownToggle nav caret>
              {choosenDept}
              <Icon name="caret-down"></Icon>
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem
                style={{ color: activeTab === 0 ? "#4461F2" : "", cursor: "pointer" }}
                onClick={() => {
                  setActiveTab(0);
                  refreshUserList();
                  setChoosenDept("All Department");
                }}
              >
                All Department
              </DropdownItem>
              <DropdownItem divider />
              {(userDetail.departments || []).map((dept, i) => (
                <DropdownItem
                  key={dept.id}
                  style={{ color: activeTab === i + 1 ? "#4461F2" : "", cursor: "pointer" }}
                  onClick={() => {
                    setActiveTab(i + 1);
                    setChoosenDept(dept.name);
                    refreshUserListDept(dept.id);
                  }}
                >
                  {dept.name}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>

          <Button
            color="transparent"
            onClick={(e) => {
              e.preventDefault();
              refreshUserList();
              setChoosenDept("All Department");
            }}
          >
            Refresh
            <Icon className="ml-1" name="reload"></Icon>
          </Button>
        </Nav>

        <TabContent activeTab={activeTab}>
          <div className="form-control-wrap">
            <div className="form-icon form-icon-left">
              <Icon name="search"></Icon>
            </div>
            <input
              type="text"
              className="form-round form-control"
              id="default-03"
              placeholder="Search user"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <SimpleBar style={{ maxHeight: "350px", minHeight: "350px" }}>
            {filteredUsers.length && searchText !== "" ? (
              filteredUsers.map((item, index) => renderUserCard(item, index))
            ) : (salesList.length && searchText === "") || (!salesList.length && searchText === "") ? (
              salesList.map((item, index) => renderUserCard(item, index))
            ) : (
              <p className="mt-4">No user matched</p>
            )}
          </SimpleBar>

          <Button
            color="primary"
            className="mt-2"
            style={{ width: "100%", justifyContent: "center" }}
            disabled={!selectedUserId || isAssigning}
            onClick={(e) => {
              e.preventDefault();
              handleAssign();
            }}
          >
            Assign
          </Button>
        </TabContent>
      </ModalBody>
    </Modal>
  );
};

export default IgChatModalAssign;
